import { useMemo, useState } from "react";
import { BookOpen, Download, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

/* Consulting-grade in-app documentation reader.
   Renders the constrained markdown used by the manual content:
   ## / ### headings, "- " bullets, "1." lists, | tables |, **bold**,
   *italic*, `code`. Left contents sidebar with live search. */

const renderInline = (text, keyBase) => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith("**")) return <b key={`${keyBase}-${i}`} className="font-semibold text-[#1E293B]">{p.slice(2, -2)}</b>;
    if (p.startsWith("`")) return <code key={`${keyBase}-${i}`} className="font-mono text-[11px] bg-slate-100 border border-slate-200 px-1">{p.slice(1, -1)}</code>;
    if (p.startsWith("*")) return <i key={`${keyBase}-${i}`}>{p.slice(1, -1)}</i>;
    return p;
  });
};

const MarkdownBody = ({ md }) => {
  const blocks = [];
  const lines = md.trim().split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trimEnd();
    if (!line.trim()) { i += 1; continue; }
    if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const cells = lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
        if (!cells.every((c) => /^[-: ]*$/.test(c))) rows.push(cells);
        i += 1;
      }
      blocks.push(
        <table key={`t-${i}`} className="w-full text-[12px] border border-slate-200 my-4">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider bg-slate-50 border-b border-slate-200 text-slate-500">
              {rows[0].map((c, ci) => <th key={ci} className="px-3 py-2 font-semibold">{renderInline(c, `th${ci}`)}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.slice(1).map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => <td key={ci} className="px-3 py-2 text-slate-600 align-top">{renderInline(c, `td${ri}${ci}`)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      );
      continue;
    }
    if (line.startsWith("### ")) {
      blocks.push(<h4 key={i} className="text-[12px] font-bold text-slate-600 mt-5 mb-1.5">{renderInline(line.slice(4), i)}</h4>);
    } else if (line.startsWith("## ")) {
      blocks.push(
        <h3 key={i} className="text-[13px] font-bold text-[#1E293B] mt-7 mb-2 pb-1.5 border-b border-slate-100 uppercase tracking-[0.08em]">
          {renderInline(line.slice(3), i)}
        </h3>
      );
    } else if (line.startsWith("- ")) {
      blocks.push(
        <div key={i} className="flex gap-2.5 text-[13px] text-slate-600 leading-relaxed my-1 pl-1">
          <span className="mt-[7px] h-1 w-1 shrink-0" style={{ background: "var(--brand-accent)" }} />
          <span>{renderInline(line.slice(2), i)}</span>
        </div>
      );
    } else if (/^\d+\.\s/.test(line)) {
      const dot = line.indexOf(".");
      blocks.push(
        <div key={i} className="flex gap-2.5 text-[13px] text-slate-600 leading-relaxed my-1 pl-1">
          <span className="font-mono font-bold text-[11px] mt-0.5" style={{ color: "var(--brand-accent-ink)" }}>{line.slice(0, dot + 1)}</span>
          <span>{renderInline(line.slice(dot + 1).trim(), i)}</span>
        </div>
      );
    } else {
      blocks.push(<p key={i} className="text-[13px] text-slate-600 leading-relaxed my-2">{renderInline(line, i)}</p>);
    }
    i += 1;
  }
  return <div>{blocks}</div>;
};

export const ManualReader = ({ data, onDownload, downloading }) => {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.sections;
    return data.sections.filter((s) =>
      s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q));
  }, [data, query]);

  if (!data) return <Skeleton className="h-96 w-full rounded-none" data-testid="manual-loading" />;
  const active = data.sections.find((s) => s.id === activeId) || filtered[0] || data.sections[0];
  const idx = data.sections.findIndex((s) => s.id === active.id);

  return (
    <div className="flex gap-6 items-start" data-testid="manual-reader">
      {/* contents sidebar */}
      <aside className="w-64 shrink-0 bg-white border border-slate-200 sticky top-4">
        <div className="px-4 py-3 border-b border-slate-200">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 flex items-center gap-1.5">
            <BookOpen className="h-3.5 w-3.5" style={{ color: "var(--brand-accent-ink)" }} /> Contents
          </p>
          <div className="relative mt-2.5">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the manual…" data-testid="manual-search-input"
              className="rounded-none h-8 pl-8 text-[12px]" />
          </div>
        </div>
        <nav className="py-1 max-h-[60vh] overflow-y-auto" data-testid="manual-contents">
          {filtered.length === 0 && (
            <p className="px-4 py-3 text-[12px] text-slate-400" data-testid="manual-search-empty">No sections match.</p>
          )}
          {filtered.map((s) => {
            const n = data.sections.findIndex((x) => x.id === s.id) + 1;
            const isActive = s.id === active.id;
            return (
              <button key={s.id} onClick={() => setActiveId(s.id)}
                data-testid={`manual-toc-${s.id}`}
                className={`w-full text-left px-4 py-2 text-[12px] border-l-2 transition-colors ${
                  isActive ? "bg-slate-50 font-semibold text-[#1E293B]" : "border-transparent text-slate-500 hover:bg-slate-50"}`}
                style={isActive ? { borderLeftColor: "var(--brand-accent)" } : {}}>
                <span className="font-mono text-[10px] mr-2" style={{ color: "var(--brand-accent-ink)" }}>
                  {String(n).padStart(2, "0")}
                </span>
                {s.title}
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-3 border-t border-slate-200">
          <Button onClick={onDownload} disabled={downloading} data-testid="manual-download-pdf"
            className="rounded-none w-full h-8 text-[11px] bg-[#1E293B] hover:bg-slate-700 text-white">
            <Download className="h-3.5 w-3.5" /> {downloading ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </aside>

      {/* section body */}
      <article className="flex-1 bg-white border border-slate-200 px-10 py-8 min-w-0" data-testid="manual-article">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-accent-ink)" }}>
          {data.product} · {data.title} · Section {idx + 1} of {data.sections.length}
        </p>
        <h2 className="text-[20px] font-bold text-[#1E293B] mt-2" data-testid="manual-section-title">{active.title}</h2>
        <div className="h-px w-14 mt-3 mb-2" style={{ background: "var(--brand-accent)" }} />
        <MarkdownBody md={active.body} />
        <div className="flex justify-between mt-10 pt-4 border-t border-slate-100">
          {idx > 0 ? (
            <button onClick={() => setActiveId(data.sections[idx - 1].id)} data-testid="manual-prev"
              className="text-[12px] text-slate-500 hover:text-slate-700">← {data.sections[idx - 1].title}</button>
          ) : <span />}
          {idx < data.sections.length - 1 ? (
            <button onClick={() => setActiveId(data.sections[idx + 1].id)} data-testid="manual-next"
              className="text-[12px] font-semibold hover:underline" style={{ color: "var(--brand-accent-ink)" }}>
              {data.sections[idx + 1].title} →</button>
          ) : <span />}
        </div>
      </article>
    </div>
  );
};
